/**
 * The study tree, grouped the way the files actually sit on disk.
 *
 * It used to group by node kind and show a derived label — "ADC", "DWI in
 * MNI152 (Affine)". Those labels read well until you notice that a real case
 * has four different files called "ADC":
 *
 *   02e8eb42_ADC.nii.gz                        the raw input
 *   sub-…_ADC_registered.nii.gz                a stray pre-existing file
 *   DWI/preprocess/sub-…_ADC.nii.gz            what prepdata wrote
 *   PWI/preprocess/sub-…_ADC.nii.gz            the PWI half's copy
 *
 * The thing that tells them apart is the folder, and the label was throwing
 * exactly that away. Four identical rows is worse than a long name: you cannot
 * pick the right one, and you cannot tell you picked the wrong one.
 *
 * So the folder is the group and the filename is the row. Both are what you
 * would see in Finder, which means what is on screen can be found on disk.
 * The derived modality stays, as a muted suffix rather than as the name.
 */

import type { CaseManifest, CaseNode } from "@/ipc/contract";

export interface TreeFolder {
  id: string;
  /** "Input", "DWI / preprocess", … */
  label: string;
  nodes: CaseNode[];
}

/** Pipeline stage order, so folders read in the order they were produced. */
const STAGE_ORDER = ["preprocess", "registration", "segment", "segmentation", "reporting", "tools"];

function stageRank(dir: string): number {
  const stage = dir.split("/").pop() ?? "";
  const i = STAGE_ORDER.indexOf(stage);
  return i === -1 ? STAGE_ORDER.length : i;
}

function modalityRank(dir: string): number {
  if (dir.startsWith("DWI")) return 0;
  if (dir.startsWith("PWI")) return 1;
  return 2;
}

/** Everything in the manifest, in one list, with its kind kept. */
export function allNodes(manifest: CaseManifest): CaseNode[] {
  return [
    ...manifest.images,
    ...manifest.masks,
    ...manifest.transforms,
    ...manifest.atlases,
    ...manifest.measurements,
    ...manifest.reports,
  ];
}

/**
 * The directory a node sits in, relative to the subject folder.
 *
 * `relPath` starts with the subject id, which is the same for every node and
 * therefore carries no information; the first segment is dropped. A file
 * directly in the subject folder has no directory, and those are the inputs.
 */
export function folderOf(node: CaseNode): string {
  const parts = node.relPath.split("/").filter(Boolean);
  // drop the subject id and the file name
  const dirs = parts.slice(1, -1);
  return dirs.join("/");
}

/**
 * The filename with the parts that carry no information removed: the
 * subject-id prefix, which every file in the case repeats, and the extension,
 * which is `.nii.gz` on almost all of them.
 *
 * What is left is the part that actually distinguishes one file from another —
 * `ADC_space-MNI152_aff_desc-norm` rather than
 * `sub-02e8eb42_ADC_space-MNI152_aff_desc-norm.nii.gz` — so the discriminator
 * survives a narrow panel instead of being truncated away.
 */
export function shortName(node: CaseNode, subjectId: string): string {
  let name = node.relPath.split("/").pop() ?? node.relPath;
  const base = subjectId.replace(/^sub-/, "");
  for (const prefix of [`${subjectId}_`, `${base}_`]) {
    if (name.startsWith(prefix)) {
      name = name.slice(prefix.length);
      break;
    }
  }
  return name.replace(/\.(nii\.gz|nii|json|csv|txt|pdf|png|mat|yaml|yml)$/i, "");
}

export function fileName(node: CaseNode): string {
  return node.relPath.split("/").pop() ?? node.relPath;
}

export function buildTree(manifest: CaseManifest): TreeFolder[] {
  const byDir = new Map<string, CaseNode[]>();
  for (const node of allNodes(manifest)) {
    const dir = folderOf(node);
    const list = byDir.get(dir);
    if (list) list.push(node);
    else byDir.set(dir, [node]);
  }

  const folders: TreeFolder[] = [...byDir.entries()].map(([dir, nodes]) => ({
    id: dir || "__input__",
    label: dir ? dir.replace(/\//g, " / ") : "Input",
    nodes: nodes.slice().sort((a, b) => fileName(a).localeCompare(fileName(b))),
  }));

  return folders.sort((a, b) => {
    // Inputs first; they are what the run started from.
    if (a.id === "__input__") return -1;
    if (b.id === "__input__") return 1;
    const dirA = a.id;
    const dirB = b.id;
    return (
      modalityRank(dirA) - modalityRank(dirB) ||
      stageRank(dirA) - stageRank(dirB) ||
      dirA.localeCompare(dirB)
    );
  });
}


/**
 * What "open" means for one kind of node.
 *
 * Every row used to carry the same two controls — a radio that makes it the
 * base volume, a checkbox that overlays it — because every row was treated as
 * a volume. A QFV table is neither, so it got no control at all and clicking
 * it did nothing, which reads as the application being broken rather than as
 * the row not being that sort of thing.
 *
 * One rule instead, covering every kind: open it, in whatever way that kind of
 * thing opens.
 */
export type OpenAction =
  /** Load it as the base volume and go to the viewer. */
  | { kind: "base" }
  /** Draw it on top of whatever is already there. */
  | { kind: "overlay" }
  /** Hand it to the application the system uses for that file type. */
  | { kind: "external" }
  /** No viewer exists for it; selecting it is the whole action. */
  | { kind: "details" };

export function openActionFor(kind: CaseNode["kind"]): OpenAction {
  switch (kind) {
    case "image":
      return { kind: "base" };
    case "mask":
    case "atlas":
      return { kind: "overlay" };
    case "table":
    case "report":
    case "figure":
      // Nothing in-app renders a CSV or a PDF, and pretending otherwise would
      // be worse than handing it to the application that does.
      return { kind: "external" };
    default:
      return { kind: "details" };
  }
}

/** The label for that action, used on the row button and in its tooltip. */
export function openLabelFor(kind: CaseNode["kind"]): string {
  switch (openActionFor(kind).kind) {
    case "base":
      return "Show in the viewer";
    case "overlay":
      return "Overlay on the current image";
    case "external":
      return kind === "table" ? "Open the table" : kind === "figure" ? "Open the figure" : "Open the report";
    default:
      return "Show details";
  }
}
